import { ControlledCheckbox } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ServiceDataResultOfBimWorkOrderTotalData } from 'gql/graphql';
import { Button, cnMixSpace, Text } from 'uikit';
import { formatDateToLocale } from 'utils';
import type { Meta } from 'utils/types';

import type { PayloadAction } from '@reduxjs/toolkit';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { bimUpdateActKC2TotalsGql } from './bimKC2Queries';

const ACTION_MESSAGE = 'Требуется выбрать дальнейшие действия для данных Актов:';

export const BimAddKC2UploadResolver = ({
    workOrderId,
    bimUploadKC2Data,
    bimUploadKC2Status,
    onClose,
    bimUploadReset,
}: {
    workOrderId: number | undefined;
    bimUploadKC2Data: Partial<ServiceDataResultOfBimWorkOrderTotalData> | undefined;
    bimUploadKC2Status: RequestStatus;
    onClose: (isSuccess: boolean) => void;
    bimUploadReset: (value: unknown) => PayloadAction<any, string, Meta, never>;
}) => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);
    const { control, handleSubmit, watch } = useForm({
        criteriaMode: 'all',
        shouldUnregister: true,
    });

    const [isInfoModalActive, setIsInfoModalActive] = useState(false);
    const [isRewriteModalActive, setIsRewriteModalActive] = useState(false);

    useEffect(() => {
        if (
            bimUploadKC2Status === RequestStatus.SUCCESS &&
            bimUploadKC2Data?.result === true &&
            bimUploadKC2Data?.message &&
            !bimUploadKC2Data?.actionRequired
        ) {
            setIsInfoModalActive(true);
        }

        if (
            bimUploadKC2Status === RequestStatus.SUCCESS &&
            bimUploadKC2Data?.result === true &&
            bimUploadKC2Data?.validation?.some((item) => item.metadata) &&
            bimUploadKC2Data?.actionRequired
        ) {
            setIsRewriteModalActive(true);
        }
    }, [
        bimUploadKC2Data?.actionRequired,
        bimUploadKC2Data?.message,
        bimUploadKC2Data?.result,
        bimUploadKC2Data?.validation,
        bimUploadKC2Status,
    ]);

    useEffect(
        () => () => {
            setIsInfoModalActive(false);
            setIsRewriteModalActive(false);
        },
        [],
    );

    const closeModalHandler = useCallback(() => {
        onClose(true);
        bimUploadReset(undefined);
    }, [bimUploadReset, onClose]);

    const { validationBodyMessage, validationBody } = useMemo(() => {
        const validationWithMeta = bimUploadKC2Data?.validation?.filter((item) => item.metadata);

        const message = validationWithMeta?.[0]?.message;

        const body = validationWithMeta?.map(({ metadata }) => {
            const parsedMeta = metadata && JSON.parse(metadata);

            return (
                <Fragment key={parsedMeta?.workOrderPriceName}>
                    <Text weight="semibold">{parsedMeta?.workOrderPriceName}</Text>

                    {parsedMeta?.actsKC2.map((KC2: any) => {
                        const label = `Акт ${KC2.number} от ${formatDateToLocale(KC2.date)}`;

                        return (
                            <div key={KC2.id} className={cnMixSpace({ m: 'm' })}>
                                <ControlledCheckbox name={`${KC2.id}`} control={control} label={label} size="m" />
                            </div>
                        );
                    })}
                </Fragment>
            );
        });

        return {
            validationBodyMessage: message,
            validationBody: body,
        };
    }, [bimUploadKC2Data?.validation, control]);

    const [, , , updateKC2Request] = useRequest({
        query: bimUpdateActKC2TotalsGql,
        requestId: 'bimUpdateActKC2Totals',
        withEffect: false,
        dependenciesUpdater: closeModalHandler,
    });

    const actionHandler = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const onSubmit = useCallback(
        (data) => {
            const actsKC2Ids = Object.keys(data).map((key) => Number(key));

            if (bimUploadKC2Data?.data?.uploadPriceActs) {
                dispatch(
                    updateKC2Request({
                        workOrderId,
                        uploadId: bimUploadKC2Data?.data?.uploadPriceActs,
                        actsKC2Ids,
                    }),
                );
            }
        },
        [bimUploadKC2Data?.data?.uploadPriceActs, dispatch, updateKC2Request, workOrderId],
    );

    const confirmBtnDisabled = Object.values(watch()).every((value) => !value);

    return (
        <>
            <ModalDialog
                title="Выберите действие"
                isOpen={isRewriteModalActive}
                onClose={closeModalHandler}
                onConfirm={actionHandler}
                btnConfirmLabel="Перезаписать Акт КС-2"
                btnCancelLabel="Пропустить"
                btnConfirmDisabled={confirmBtnDisabled}
                isOnClickOutsideDisable
                withTitleCloseButton
                bodyMinHeight="500px"
                bodyMaxHeight="500px"
                style={{ maxWidth: 550 }}
            >
                <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                    <div className={cnMixSpace({ m: 'xl' })} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text>{validationBodyMessage}</Text>
                        <Text className={cnMixSpace({ mB: 'xl' })}>{ACTION_MESSAGE}</Text>

                        {validationBody}
                    </div>
                </form>
            </ModalDialog>

            <UploadStatus
                isOpened={isInfoModalActive}
                handleCloseModal={closeModalHandler}
                data={bimUploadKC2Data}
                titleText="Результат проверки"
                status="error"
                withTitleCloseButton={false}
            >
                <Button label="Понятно" onClick={closeModalHandler} />
            </UploadStatus>
        </>
    );
};

BimAddKC2UploadResolver.displayName = 'BimAddKC2UploadResolver';
